// Making zones in code: lay down ground (grass, paths, stone, water, soil, piers, sand), then place trees,
// lamps, houses, fences and the rest. The result is the collision the server uses and an art list the client
// paints in the same pixel style as the town. Same input, same zone, on every machine.

export const T = { GRASS: 0, PATH: 1, STONE: 2, WATER: 3, SOIL: 4, PIER: 5, SAND: 6 } as const;

export type PropKind = 'oak' | 'blossom' | 'pine' | 'bush' | 'tuft' | 'mushroom' | 'rock' | 'reeds' | 'lamp' | 'bench' | 'barrel' | 'crate' | 'pot'
  | 'fence_h' | 'fence_v' | 'sign' | 'stall' | 'house' | 'boat' | 'cabbage' | 'carrot' | 'lily' | 'wayshrine' | 'well' | 'anvil' | 'arch';
export interface HouseDef { w: number; h: number; roof: string; roofD: string; door: number; icon: string; name: string }
export interface PropDef { kind: PropKind; x: number; y: number; v?: number; house?: HouseDef }
export interface ZoneArt { w: number; h: number; ground: string; props: PropDef[] }

/** Props you can jump up onto, and how high they are (in 4-pixel steps). Everything else solid is a wall. */
const PLATFORM: Partial<Record<PropKind, number>> = { crate: 3, barrel: 3, rock: 2, bush: 3, fence_h: 3, fence_v: 3, anvil: 2, well: 5 };

/** A small seeded random generator, so scattering is the same everywhere. */
export function rng(seed: number): () => number { let s = seed >>> 0; return () => { s = (s * 1664525 + 1013904223) >>> 0; return s / 4294967296; }; }

export class ZoneBuilder {
  readonly g: Uint8Array; readonly b: Uint8Array; readonly props: PropDef[] = [];
  /** Things you can jump onto: height in 4-pixel steps (0 = a wall, if blocked). */
  readonly hgt: Uint8Array;
  constructor(readonly w: number, readonly h: number, base: number = T.GRASS) { this.g = new Uint8Array(w * h).fill(base); this.b = new Uint8Array(w * h); this.hgt = new Uint8Array(w * h); }

  in(x: number, y: number): boolean { return x >= 0 && y >= 0 && x < this.w && y < this.h; }
  at(x: number, y: number): number { return this.in(x, y) ? this.g[y * this.w + x] : -1; }
  blocked(x: number, y: number): boolean { return !this.in(x, y) || this.b[y * this.w + x] === 1; }
  fill(x0: number, y0: number, x1: number, y1: number, t: number): this { for (let y = y0; y <= y1; y++) for (let x = x0; x <= x1; x++) if (this.in(x, y)) this.g[y * this.w + x] = t; return this; }
  ellipse(cx: number, cy: number, rx: number, ry: number, t: number): this {
    for (let y = Math.floor(cy - ry); y <= cy + ry; y++) for (let x = Math.floor(cx - rx); x <= cx + rx; x++) if (((x + 0.5 - cx) / rx) ** 2 + ((y + 0.5 - cy) / ry) ** 2 <= 1 && this.in(x, y)) this.g[y * this.w + x] = t;
    return this;
  }
  block(x0: number, y0: number, x1 = x0, y1 = y0): this { for (let y = y0; y <= y1; y++) for (let x = x0; x <= x1; x++) if (this.in(x, y)) this.b[y * this.w + x] = 1; return this; }
  unblock(x0: number, y0: number, x1 = x0, y1 = y0): this { for (let y = y0; y <= y1; y++) for (let x = x0; x <= x1; x++) if (this.in(x, y)) this.b[y * this.w + x] = 0; return this; }
  free(x: number, y: number): boolean { return this.in(x, y) && !this.b[y * this.w + x] && this.at(x, y) !== T.WATER; }
  put(kind: PropKind, x: number, y: number, v?: number, solid = true): this {
    this.props.push({ kind, x, y, v });
    if (solid) { this.block(x, y); const h = PLATFORM[kind]; if (h && this.in(x, y)) this.hgt[y * this.w + x] = h; }
    return this;
  }

  tree(x: number, y: number, kind?: 'oak' | 'blossom' | 'pine'): this {
    if (this.at(x, y) === T.PATH || this.at(x, y) === T.WATER) return this;
    const h = ((x * 73856093) ^ (y * 19349663)) >>> 0;
    return this.put(kind ?? (h % 5 === 0 ? 'pine' : h % 4 === 1 ? 'blossom' : 'oak'), x, y);
  }
  /** Trees all around the edge (every other tile, like the town), leaving the gaps open. */
  border(gaps: [number, number, number, number][] = []): this {
    const gap = (x: number, y: number) => gaps.some(([x0, y0, x1, y1]) => x >= x0 && x <= x1 && y >= y0 && y <= y1);
    for (let x = 0; x < this.w; x++) for (const y of [0, this.h - 1]) if (!gap(x, y)) this.block(x, y);
    for (let y = 0; y < this.h; y++) for (const x of [0, this.w - 1]) if (!gap(x, y)) this.block(x, y);
    for (let x = 1; x < this.w - 1; x += 2) { if (!gap(x, 1)) this.tree(x, 1); if (!gap(x, this.h - 2)) this.tree(x, this.h - 2); }
    for (let y = 3; y < this.h - 2; y += 2) { if (!gap(1, y)) this.tree(1, y); if (!gap(this.w - 2, y)) this.tree(this.w - 2, y); }
    return this;
  }
  lamp(x: number, y: number): this { return this.put('lamp', x, y); }
  bench(x: number, y: number): this { this.props.push({ kind: 'bench', x, y }); this.block(x, y, x + 1, y); for (const bx of [x, x + 1]) if (this.in(bx, y)) this.hgt[y * this.w + bx] = 2; return this; }
  stall(x: number, y: number): this { this.props.push({ kind: 'stall', x, y }); return this.block(x, y - 2, x + 2, y); }
  house(x: number, y: number, h: HouseDef): this {
    this.props.push({ kind: 'house', x, y, house: h });
    this.block(x, y, x + h.w - 1, y + h.h - 1);
    return this;
  }
  /** A path from a house door down to a row. */
  doorPath(x: number, y: number, h: HouseDef, toY: number): this { const dx = x + h.door; return this.fill(dx, y + h.h, dx, toY, T.PATH); }
  fenceH(x0: number, x1: number, y: number, gaps: number[] = []): this { for (let x = x0; x <= x1; x++) if (!gaps.includes(x)) this.put('fence_h', x, y); return this; }
  fenceV(x: number, y0: number, y1: number): this { for (let y = y0; y <= y1; y++) this.put('fence_v', x, y); return this; }
  /** Sprinkle small things on open grass. */
  scatter(seed: number, count: number, kinds: PropKind[], area: [number, number, number, number] = [2, 2, this.w - 3, this.h - 3], solidKinds: PropKind[] = ['bush', 'rock']): this {
    const r = rng(seed), [x0, y0, x1, y1] = area;
    for (let i = 0; i < count; i++) {
      const x = x0 + Math.floor(r() * (x1 - x0 + 1)), y = y0 + Math.floor(r() * (y1 - y0 + 1)), k = kinds[Math.floor(r() * kinds.length)];
      if (this.at(x, y) !== T.GRASS || this.b[y * this.w + x]) continue;
      this.put(k, x, y, Math.floor(r() * 3), solidKinds.includes(k));
    }
    return this;
  }
  /** Water is never walkable; piers over it are. */
  finish(): { collision: string[]; art: ZoneArt } {
    const collision: string[] = [];
    for (let y = 0; y < this.h; y++) {
      let row = '';
      for (let x = 0; x < this.w; x++) {
        const i = y * this.w + x;
        row += this.g[i] === T.WATER && !this.b[i] ? '~' : !this.b[i] ? '.' : this.hgt[i] ? String(this.hgt[i]) : '#';
      }
      collision.push(row);
    }
    return { collision, art: { w: this.w, h: this.h, ground: Array.from(this.g).join(''), props: this.props } };
  }
}
